import { useCallback, useEffect, useRef, useState } from 'react';

import { SplashScreen as ExpoSplashScreen } from 'expo-router';
import { getAppTakeover } from 'features/app-takeover/hooks/use-app-takeover';
import { maybePlayAudioOnce } from 'features/app-takeover/utils/play-audio-once';
import { Platform } from 'react-native';

import { AnimatedSplashScreen } from 'components/splash-screen/splash-screen';
import {
  animatedSplashScreenHiddenDeferred,
  nativeSplashScreenHiddenDeferred,
} from 'utils/hide-splash-screen';
import logger from 'utils/logger';
import { useLottieSource } from 'utils/lottie/use-lottie-source';
import type { LottieSource } from 'utils/lottie/use-lottie-source.types';

const bkBounceSplashJson = require('../assets/splash-screen.json');

export const LOTTIE_SPLASH_SCREEN_DURATION_MS = 1800;
export const WIGGLE_ROOM = 800;

export const useStartupSplashController = () => {
  const shouldExtendSplashForFreshStart = useRef(
    Platform.OS !== 'web' && !AnimatedSplashScreen.getHideSplashPreInit()
  ).current;
  const [animatedSplashVisible, setAnimatedSplashVisible] = useState(
    shouldExtendSplashForFreshStart
  );
  const [isSplashAnimationComplete, setIsSplashAnimationComplete] = useState(
    !shouldExtendSplashForFreshStart
  );
  const [isAppReadyToHideSplash, setIsAppReadyToHideSplash] = useState(
    !shouldExtendSplashForFreshStart
  );
  const isSplashAnimationCompleteRef = useRef(!shouldExtendSplashForFreshStart);
  const isAppReadyToHideSplashRef = useRef(!shouldExtendSplashForFreshStart);
  const [shouldUseBundledStartupSplash, setShouldUseBundledStartupSplash] = useState(false);

  const {
    lottie: appTakeoverLottie,
    audio: appTakeoverAudio,
    video: appTakeoverVideo,
    durationMs: appTakeoverDurationMs,
  } = getAppTakeover()?.splashScreen || {};

  const splashScreenDuration =
    (appTakeoverDurationMs ?? LOTTIE_SPLASH_SCREEN_DURATION_MS) + WIGGLE_ROOM;
  const { source: splashAnimationSource, error: splashPreparationError } = useLottieSource({
    source: appTakeoverVideo ? undefined : appTakeoverLottie || bkBounceSplashJson,
    surface: 'app_splash',
  });
  const shouldShowSplashVideo = Boolean(
    shouldExtendSplashForFreshStart && appTakeoverVideo && !shouldUseBundledStartupSplash
  );
  const visibleSplashAnimationSource = shouldUseBundledStartupSplash
    ? bkBounceSplashJson
    : splashAnimationSource;
  const shouldShowStaticStartupSplash =
    !shouldShowSplashVideo && (!visibleSplashAnimationSource || isSplashAnimationComplete);

  const hideAnimatedSplash = useCallback(() => {
    setAnimatedSplashVisible(oldVal => {
      if (oldVal) {
        animatedSplashScreenHiddenDeferred.resolve();
        return false;
      }
      return oldVal;
    });
  }, []);

  const handleSplashAnimationComplete = useCallback(() => {
    isSplashAnimationCompleteRef.current = true;
    setIsSplashAnimationComplete(true);
    if (isAppReadyToHideSplashRef.current) {
      hideAnimatedSplash();
    }
  }, [hideAnimatedSplash]);

  const handleAppReadyToHideSplash = useCallback(() => {
    isAppReadyToHideSplashRef.current = true;
    setIsAppReadyToHideSplash(true);
    if (isSplashAnimationCompleteRef.current) {
      hideAnimatedSplash();
    }
  }, [hideAnimatedSplash]);

  const handleSplashVideoFallback = useCallback(() => {
    setShouldUseBundledStartupSplash(true);
    handleSplashAnimationComplete();
  }, [handleSplashAnimationComplete]);

  useEffect(() => {
    if (appTakeoverAudio) {
      maybePlayAudioOnce(appTakeoverAudio);
    }

    if (Platform.OS === 'web') {
      // no splashscreen on web but we need to resolve these promises which other components depend on.
      nativeSplashScreenHiddenDeferred.resolve();
      animatedSplashScreenHiddenDeferred.resolve();
      return;
    }

    // required for deep linking situations
    ExpoSplashScreen.hideAsync().finally(() => nativeSplashScreenHiddenDeferred.resolve());

    if (!shouldExtendSplashForFreshStart) {
      return;
    }

    const animationTimer = setTimeout(() => {
      if (!isSplashAnimationCompleteRef.current) {
        logger.warn({
          message: `Splash screen animation took longer than ${splashScreenDuration}ms. This SHOULD NOT happen and should be investigated but it has been forced to continue for this session.`,
        });
        handleSplashAnimationComplete();
      }
    }, splashScreenDuration);

    return () => {
      clearTimeout(animationTimer);
    };
  }, [
    appTakeoverAudio,
    handleSplashAnimationComplete,
    shouldExtendSplashForFreshStart,
    splashScreenDuration,
  ]);

  useEffect(() => {
    if (splashPreparationError && shouldExtendSplashForFreshStart) {
      setShouldUseBundledStartupSplash(true);
    }
  }, [shouldExtendSplashForFreshStart, splashPreparationError]);

  useEffect(() => {
    if (shouldExtendSplashForFreshStart && isSplashAnimationComplete && isAppReadyToHideSplash) {
      hideAnimatedSplash();
    }
  }, [
    hideAnimatedSplash,
    isAppReadyToHideSplash,
    isSplashAnimationComplete,
    shouldExtendSplashForFreshStart,
  ]);

  return {
    animatedSplashVisible,
    handleAppReadyToHideSplash: shouldExtendSplashForFreshStart
      ? handleAppReadyToHideSplash
      : undefined,
    handleSplashAnimationComplete,
    handleSplashVideoFallback,
    shouldShowSplashVideo,
    shouldShowStaticStartupSplash,
    splashVideoSource: appTakeoverVideo,
    visibleSplashAnimationSource: visibleSplashAnimationSource as LottieSource,
  };
};
